import { Dispatch, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';
import { DialQueries } from '@frontend/api-dialpad';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconColorType } from '@frontend/icons';
import { usePhoneConfigStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { SchemaPhoneUserService } from '@frontend/schema';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  CaretDownIcon,
  NakedButton,
  PopoverMenu,
  PopoverMenuItem,
  Text,
  TextLink,
  Truncated,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';
import { OutboundSelectedPhoneNumberProps } from '..';
import { formatPhoneNumber } from '../utils/phone-utils';

export const THEME_STYLES = {
  DARK: 'dark',
  LIGHT: 'light',
} as const;

type ThemeStyle = (typeof THEME_STYLES)[keyof typeof THEME_STYLES];

export type DeviceInformationProps = {
  deviceName: string;
  onChangeDeviceClick: () => void;
  changeDeviceText: string;
  showExtension: boolean;
  showOutboundCallerInfo?: boolean;
  extensionNumberText: string;
  currentPhoneNumberText: string | null;
  availablePhoneNumbers: { number: string; locationName: string }[];
  SelectOutboundIdMenuWrapper: React.FC<SelectOutboundIdMenuProps>;
  isPreferredNumber: boolean;
  themeStyle?: ThemeStyle;
  trackingId?: string;
};

const starIconSize = 12;

export const DeviceInformation = ({
  deviceName,
  onChangeDeviceClick,
  changeDeviceText,
  showExtension,
  extensionNumberText,
  currentPhoneNumberText,
  availablePhoneNumbers,
  SelectOutboundIdMenuWrapper,
  themeStyle = THEME_STYLES.DARK,
  showOutboundCallerInfo,
  isPreferredNumber,
  trackingId,
}: DeviceInformationProps) => {
  const { t } = useTranslation('phone');
  const showPreferredIcon = isPreferredNumber && availablePhoneNumbers.length > 1;
  const { getTriggerProps, getMenuProps, getItemProps, close, activeIndex, setActiveIndex } = usePopoverMenu({
    placement: 'bottom',
    interactionOptions: {
      listNavigation: {
        activeIndex: 0,
        onNavigate: (index) => {
          // This (in combination with initialFocus) makes it so that the first item is always focused when the menu opens
          // (the menu is never unmounted, but the activeIndex resets to null if we don't do this)
          if (index === null) {
            setActiveIndex(0);
          } else {
            setActiveIndex(index);
          }
        },
      },
      typeahead: {
        enabled: false,
      },
    },
    middlewareOptions: {
      offset: ({ rects }) => {
        // this is a magic number we would need to figure out to be more dynamic to
        // 8 is a magic that matches the two css "gaps" we set below. 4 + 4.
        return {
          crossAxis: rects.reference.width / 2 - starIconSize - 8,
        };
      },
    },
  });

  const actionsTriggerProps = getTriggerProps({
    onClick: (e) => {
      e.stopPropagation();
    },
  });

  const textColor = themeStyle === THEME_STYLES.DARK ? 'white' : 'default';

  return (
    <>
      <div
        css={css`
          display: flex;
          gap: ${theme.spacing(1)};
        `}
      >
        <Truncated as={Text} weight='bold' color={textColor} css={{ maxWidth: 175 }}>
          {deviceName}
        </Truncated>
        <TextLink
          size='small'
          weight='bold'
          css={{ flexShrink: 0 }}
          onClick={onChangeDeviceClick}
          trackingId={trackingId}
        >
          {changeDeviceText}
        </TextLink>
      </div>

      {showExtension && (
        <Text
          size='medium'
          css={css`
            color: ${theme.colors.neutral30};
          `}
        >
          {extensionNumberText}
        </Text>
      )}

      {showOutboundCallerInfo && (
        <div css={{ width: 'fit-content' }}>
          <div
            style={{
              display: 'flex',
              gap: theme.spacing(0.5),
            }}
          >
            <Text
              size='medium'
              css={css`
                color: ${themeStyle === THEME_STYLES.DARK ? theme.colors.neutral30 : theme.colors.neutral50};
              `}
            >
              {t('Outbound ID')}:
            </Text>
            <div
              {...actionsTriggerProps}
              css={{
                display: 'flex',
                alignItems: 'center',
                cursor: availablePhoneNumbers.length > 1 ? 'pointer' : 'default',
                gap: theme.spacing(0.5),
              }}
            >
              {showPreferredIcon && <Icon size={starIconSize} name='star-filled' color='warn' />}
              <Text
                size='medium'
                style={{
                  color: themeStyle === THEME_STYLES.DARK ? theme.colors.neutral30 : theme.colors.neutral50,
                }}
              >
                {currentPhoneNumberText}
              </Text>
              {availablePhoneNumbers.length > 1 && (
                <div
                  css={css`
                    cursor: pointer;
                  `}
                  data-trackingid='phones-generic-dialpad-modal-outboundID'
                >
                  <CaretDownIcon size={8} color={textColor} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {availablePhoneNumbers.length > 1 && (
        <SelectOutboundIdMenuWrapper
          activeIndex={activeIndex}
          getItemProps={getItemProps}
          getMenuProps={getMenuProps}
          close={close}
        />
      )}
    </>
  );
};

export type SelectOutboundIdMenuProps = {
  getMenuProps: ReturnType<typeof usePopoverMenu>['getMenuProps'];
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  close: ReturnType<typeof usePopoverMenu>['close'];
  themeStyle?: 'dark' | 'light';
  activeIndex: number | null;
};

type SelectedOutboundPhoneNumberProps = {
  availablePhoneNumbers: OutboundSelectedPhoneNumberProps[];
  currentPhoneNumber: OutboundSelectedPhoneNumberProps | undefined;
  setCurrentPhoneNumber: Dispatch<SetStateAction<OutboundSelectedPhoneNumberProps | undefined>>;
};

type ActionItemProps = OutboundSelectedPhoneNumberProps & {
  onClick: () => void;
};

type ReducedItemProps = {
  [key: string]: ActionItemProps[];
};

export const SelectOutboundIdMenu = ({
  getMenuProps,
  getItemProps,
  close: closePopover,
  availablePhoneNumbers,
  currentPhoneNumber,
  setCurrentPhoneNumber,
  themeStyle = 'dark',
  activeIndex,
}: SelectOutboundIdMenuProps & SelectedOutboundPhoneNumberProps) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasPhoneNumbersNameIdFlag = getFeatureFlagValue('phone-numbers-name-id');

  const actions: ReducedItemProps = useMemo(
    () =>
      availablePhoneNumbers.reduce((acc, curr) => {
        if (acc[curr.locationName]) {
          acc[curr.locationName].push({
            ...curr,
            onClick: () => {
              closePopover?.();
              setCurrentPhoneNumber({ ...curr });
            },
          });
        } else {
          acc[curr.locationName] = [
            {
              ...curr,
              onClick: () => {
                closePopover?.();
                setCurrentPhoneNumber({ ...curr });
              },
            },
          ];
        }

        return acc;
      }, {} as ReducedItemProps),
    [availablePhoneNumbers, closePopover, setCurrentPhoneNumber]
  );

  const backgroundColor = themeStyle === 'dark' ? theme.colors.neutral80 : theme.colors.white;
  const textColor = themeStyle === 'dark' ? 'white' : 'default';
  const hoverFocusColor = themeStyle === 'dark' ? theme.colors.neutral70 : theme.colors.neutral10;
  let counter = 0;

  return (
    <PopoverMenu
      {...getMenuProps()}
      initialFocus={0}
      css={css`
        background-color: ${backgroundColor};
        min-width: 220px;
        max-width: 220px;
        .select-outbound-id-menu-item span {
          color: ${theme.colors.white};
        }
        .select-outbound-id-menu-item:hover {
          background-color: ${hoverFocusColor};
        }
        .select-outbound-id-menu-item:focus {
          background-color: ${hoverFocusColor};
        }
      `}
    >
      {Object.entries(actions).map(([locationName, actions]) => (
        <div key={locationName}>
          <Truncated as={Text} color={textColor} weight='bold' css={{ padding: theme.spacing(1, 2) }}>
            {locationName}
          </Truncated>
          {actions.map(({ phoneNumberName, onClick, number, isPreferred, phoneNumberId }: ActionItemProps) => {
            const index = counter++;
            const itemProps = getItemProps({
              index,
              onClick,
            });

            return (
              <PopoverMenuItem
                tabIndex={activeIndex === index ? 0 : -1}
                className='select-outbound-id-menu-item'
                preserveCasing
                css={css`
                  display: grid;
                  grid-template-areas: ${phoneNumberName ? "'location icon' 'number icon'" : "'number icon'"};
                  grid-template-columns: 1fr 24px;
                  padding-top: ${theme.spacing(1)};
                  padding-bottom: ${theme.spacing(1)};
                  text-align: left;
                  align-items: center;
                  justify-content: space-between;
                  background-color: ${backgroundColor};
                  gap: 0px;
                  height: auto;

                  :hover .set-preferred-number {
                    visibility: visible;
                  }
                `}
                key={number}
                {...itemProps}
              >
                {hasPhoneNumbersNameIdFlag && phoneNumberName && (
                  <Truncated as='div' color={textColor} css={{ color: textColor, gridArea: 'location' }}>
                    {phoneNumberName}
                  </Truncated>
                )}
                <Text as='div' color={textColor} css={{ gridArea: 'number' }}>
                  {formatPhoneNumber(number ?? '')}
                </Text>
                <StarIcon
                  isPreferred={isPreferred}
                  phoneNumberId={phoneNumberId}
                  textColor={textColor}
                  setCurrentPhoneNumber={setCurrentPhoneNumber}
                  currentPhoneNumber={currentPhoneNumber}
                  hasPhoneNumbersNameIdFlag={hasPhoneNumbersNameIdFlag}
                />
              </PopoverMenuItem>
            );
          })}
        </div>
      ))}
    </PopoverMenu>
  );
};

type StarToolTip = {
  tooltipProps: ReturnType<typeof useTooltip>['tooltipProps'];
  Tooltip: ReturnType<typeof useTooltip>['Tooltip'];
  tooltipMessage: string;
};

const StarIcon = ({
  isPreferred,
  phoneNumberId,
  textColor,
  setCurrentPhoneNumber,
  currentPhoneNumber,
  hasPhoneNumbersNameIdFlag,
}: {
  isPreferred: boolean;
  phoneNumberId: string;
  textColor: IconColorType | undefined;
  currentPhoneNumber: OutboundSelectedPhoneNumberProps | undefined;
  setCurrentPhoneNumber: Dispatch<SetStateAction<OutboundSelectedPhoneNumberProps | undefined>>;
  hasPhoneNumbersNameIdFlag: boolean;
}) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const queryClient = useQueryClient();
  const phoneConfigSipProfileId = usePhoneConfigStore((store) => store.phoneConfig?.sipProfileId);
  const tenantId = usePhoneConfigStore((store) => store.phoneConfig?.tenantId);

  const tooltipMessage =
    !isPreferred && !!phoneNumberId ? 'Make preferred number' : isPreferred ? 'Preferred number' : 'Not available';

  const setPreferredNumber = useMutation({
    mutationFn: (phoneNumberId: string) => SchemaPhoneUserService.SetPreferredPhoneNumber({ phoneNumberId }),
    onSuccess: ({ phoneNumberId }) => {
      setCurrentPhoneNumber((prev) => {
        if (!prev) return;
        return {
          ...prev,
          isPreferred: phoneNumberId === currentPhoneNumber?.phoneNumberId ? true : false,
        };
      });
      if (hasPhoneNumbersNameIdFlag) {
        queryClient.invalidateQueries(DialQueries.queryKeys.getNumbersBySipProfileId(phoneConfigSipProfileId ?? ''));
      } else {
        queryClient.invalidateQueries(DialQueries.queryKeys.outboundNumbers(tenantId ?? ''));
      }
    },
  });

  return (
    <>
      <div css={{ gridArea: 'icon', height: 24 }} {...triggerProps}>
        {isPreferred && (
          <Icon
            name='star-filled'
            onClick={(e) => {
              // stop the menu from closing
              e.stopPropagation();
            }}
            color='warn'
          />
        )}
        {!isPreferred && (
          <NakedButton
            className='set-preferred-number'
            css={{
              visibility: 'hidden',
            }}
            disabled={!phoneNumberId}
            onClick={(e) => {
              e.stopPropagation();
              if (phoneNumberId) {
                setPreferredNumber.mutate(phoneNumberId);
              }
            }}
          >
            <Icon name='star' color={phoneNumberId ? textColor : 'disabled'} />
          </NakedButton>
        )}
      </div>
      <StarToolTip tooltipMessage={tooltipMessage} tooltipProps={tooltipProps} Tooltip={Tooltip} />
    </>
  );
};

const StarToolTip = ({ tooltipProps, Tooltip, tooltipMessage }: StarToolTip) => {
  return (
    <Tooltip {...tooltipProps}>
      <Text size='small' color='white'>
        {tooltipMessage}
      </Text>
    </Tooltip>
  );
};
